// Printable invoice template — Senegalese e-invoicing compliant
// Uses @media print styles from invoice-print.css

import { useTranslation } from "react-i18next";
import type { Invoice, InvoiceConfig } from "@/lib/invoicing/types";
import { InvoiceStatus } from "@/lib/invoicing/types";
import { getCompanyDetails } from "@/lib/invoicing/invoice-service";
import { generateInvoiceQR } from "@/lib/invoicing/qr-generator";

interface InvoiceTemplateProps {
  readonly invoice: Invoice;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("fr-SN", {
    style: "decimal",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function StatusBadge({
  status,
  label,
}: {
  readonly status: InvoiceStatus;
  readonly label: string;
}) {
  const colorMap: Record<InvoiceStatus, string> = {
    [InvoiceStatus.DRAFT]: "bg-stone-100 text-stone-700",
    [InvoiceStatus.ISSUED]: "bg-amber-50 text-amber-700",
    [InvoiceStatus.PAID]: "bg-emerald-50 text-emerald-700",
    [InvoiceStatus.CANCELLED]: "bg-red-50 text-red-700",
    [InvoiceStatus.REFUNDED]: "bg-violet-50 text-violet-700",
  };

  return (
    <span
      className={`inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-medium ${colorMap[status]}`}
    >
      {label}
    </span>
  );
}

export function InvoiceTemplate({ invoice }: InvoiceTemplateProps) {
  const { t } = useTranslation("invoicing");
  const config: InvoiceConfig = getCompanyDetails();
  const qrDataUrl = generateInvoiceQR(invoice);

  const statusKey = invoice.status.toLowerCase() as Lowercase<InvoiceStatus>;

  return (
    <div
      id="invoice-template"
      className="mx-auto max-w-[800px] bg-white p-8 font-['Plus_Jakarta_Sans',sans-serif] text-stone-800 print:p-0 print:shadow-none"
    >
      {/* ------------------------------------------------------------------ */}
      {/* Header                                                             */}
      {/* ------------------------------------------------------------------ */}
      <header className="mb-8 flex items-start justify-between border-b border-stone-200 pb-6">
        {/* Company block */}
        <div>
          <h2 className="font-['Cormorant_Garamond',serif] text-2xl font-bold text-amber-800">
            {config.companyName}
          </h2>
          <p className="mt-1 text-sm text-stone-500">{config.companyAddress}</p>
          <p className="text-sm text-stone-500">{config.companyPhone}</p>
          <p className="text-sm text-stone-500">{config.companyEmail}</p>
          <p className="mt-2 text-xs text-stone-500">
            <span className="font-semibold text-stone-700">{t("company.ninea")} :</span>{" "}
            {config.ninea}
          </p>
          <p className="text-xs text-stone-500">
            <span className="font-semibold text-stone-700">{t("company.rc")} :</span>{" "}
            {config.rcNumber}
          </p>
        </div>

        {/* Invoice meta */}
        <div className="text-right">
          <h1 className="font-['Cormorant_Garamond',serif] text-3xl font-bold tracking-tight text-stone-900">
            {t("title")}
          </h1>
          <p className="mt-2 text-sm text-stone-500">
            {t("number")}: <span className="font-medium text-stone-800">{invoice.number}</span>
          </p>
          <p className="text-sm text-stone-500">
            {t("date")}: <span className="font-medium text-stone-800">{formatDate(invoice.date)}</span>
          </p>
          <p className="text-sm text-stone-500">
            {t("due_date")}:{" "}
            <span className="font-medium text-stone-800">{formatDate(invoice.dueDate)}</span>
          </p>
          <div className="mt-2 no-print">
            <StatusBadge status={invoice.status} label={t(`status.${statusKey}`)} />
          </div>
        </div>
      </header>

      {/* ------------------------------------------------------------------ */}
      {/* Customer                                                           */}
      {/* ------------------------------------------------------------------ */}
      <section className="mb-8">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-stone-400">
          {t("customer")}
        </h3>
        <div className="rounded-lg border border-stone-100 bg-stone-50/50 p-4">
          <p className="font-medium text-stone-800">{invoice.customer.name}</p>
          <p className="text-sm text-stone-600">{invoice.customer.address}</p>
          <p className="text-sm text-stone-600">
            {invoice.customer.city}, {invoice.customer.region}
          </p>
          <p className="text-sm text-stone-600">{invoice.customer.phone}</p>
          {invoice.customer.email && (
            <p className="text-sm text-stone-600">{invoice.customer.email}</p>
          )}
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Items table                                                        */}
      {/* ------------------------------------------------------------------ */}
      <section className="mb-8">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-stone-200">
              <th className="pb-2 text-left font-semibold text-stone-600">
                {t("items_table.description")}
              </th>
              <th className="pb-2 text-right font-semibold text-stone-600">
                {t("items_table.quantity")}
              </th>
              <th className="pb-2 text-right font-semibold text-stone-600">
                {t("items_table.unit_price")}
              </th>
              <th className="pb-2 text-right font-semibold text-stone-600">
                {t("items_table.total")}
              </th>
            </tr>
          </thead>
          <tbody>
            {invoice.items.map((item, idx) => (
              <tr
                key={idx}
                className="border-b border-stone-100 last:border-b-0"
              >
                <td className="py-3 text-stone-800">{item.description}</td>
                <td className="py-3 text-right text-stone-600">
                  {item.quantity}
                </td>
                <td className="py-3 text-right text-stone-600">
                  {formatCurrency(item.unitPrice)} FCFA
                </td>
                <td className="py-3 text-right font-medium text-stone-800">
                  {formatCurrency(item.totalPrice)} FCFA
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Summary                                                            */}
      {/* ------------------------------------------------------------------ */}
      <section className="mb-8 flex justify-end">
        <div className="w-72 space-y-2">
          <div className="flex justify-between text-sm text-stone-600">
            <span>{t("summary.subtotal_ht")}</span>
            <span>{formatCurrency(invoice.subtotal)} FCFA</span>
          </div>
          <div className="flex justify-between text-sm text-stone-600">
            <span>{t("summary.tva")}</span>
            <span>{formatCurrency(invoice.taxAmount)} FCFA</span>
          </div>
          <div className="flex justify-between border-t-2 border-stone-800 pt-2 text-base font-bold text-stone-900">
            <span>{t("summary.total_ttc")}</span>
            <span>{formatCurrency(invoice.total)} FCFA</span>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Notes                                                              */}
      {/* ------------------------------------------------------------------ */}
      {invoice.notes && (
        <section className="mb-8 rounded-lg border border-stone-100 bg-stone-50/50 p-4 text-sm text-stone-600">
          {invoice.notes}
        </section>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Footer — legal info + QR                                           */}
      {/* ------------------------------------------------------------------ */}
      <footer className="mt-8 border-t border-stone-200 pt-6">
        <div className="flex items-end justify-between">
          <div className="space-y-1 text-xs text-stone-400">
            <p>{t("company.legal_notice")}</p>
            <p>
              {t("company.ninea")}: {config.ninea} &middot;{" "}
              {t("company.rc")}: {config.rcNumber}
            </p>
            <p>{t("company.payment_terms")}</p>
          </div>

          <div className="flex flex-col items-center">
            <img
              src={qrDataUrl}
              alt={t("qr.scan_to_verify")}
              width={112}
              height={112}
              className="h-28 w-28"
            />
            <p className="mt-1 text-[10px] text-stone-400">
              {t("qr.scan_to_verify")}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
